/**
 * Prioris — Spec Library admin page.
 *
 * Per-project requirement library. Admin can add/edit/delete rules that are
 * then auto-checked against every submittal in the project.
 */
import { useEffect, useState, useMemo } from 'react';
import { fetchSln } from '../nexus/nexusHelpers';
import { useAuth } from '../../contexts/AuthContext';
import {
  Plus, Trash2, ShieldCheck, Loader2, AlertCircle, ChevronRight, X, Check, FileUp, Sparkles
} from 'lucide-react';

const OPERATORS = [
  { v: '>=',      label: '≥ at least' },
  { v: '<=',      label: '≤ at most' },
  { v: '=',       label: '= equal to' },
  { v: '!=',      label: '≠ not equal' },
  { v: 'between', label: 'between' },
  { v: 'in',      label: 'in list' },
  { v: 'not_in',  label: 'not in list' },
];

const FAMILY_FIELD_HINTS = {
  concrete_mix: [
    { key: 'cement_content',  label: 'Cement content (kg/m³)' },
    { key: '_wcm',            label: 'w/cm ratio (computed)' },
    { key: '_scm_pct',        label: 'SCM % (computed)' },
    { key: '_total_binder',   label: 'Total binder (kg/m³)' },
    { key: 'water_content',   label: 'Water content (kg/m³)' },
    { key: 'fly_ash_content', label: 'Fly ash (kg/m³)' },
    { key: 'ggbs_content',    label: 'GGBS (kg/m³)' },
    { key: 'silica_fume_content', label: 'Silica fume (kg/m³)' },
    { key: 'cement_type',     label: 'Cement type' },
    { key: 'declared_fc28',   label: 'Declared fc,28 (MPa)' },
  ],
};

export default function MaSpecLibrary({ t, project, onOpenPdfIngest }) {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const projectId = project?.id;

  const [reqs, setReqs] = useState([]);
  const [families, setFamilies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [familyFilter, setFamilyFilter] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [r, f] = await Promise.all([
        fetchSln(`/api/prioris/ma/spec/requirements?project_id=${projectId}`),
        fetchSln('/api/prioris/ma/families'),
      ]);
      setReqs(r.requirements || []);
      setFamilies(f.families || []);
    } catch {}
    setLoading(false);
  };
  useEffect(() => { if (projectId) load(); /* eslint-disable-next-line */ }, [projectId]);

  const grouped = useMemo(() => {
    const filtered = familyFilter ? reqs.filter(r => r.family === familyFilter) : reqs;
    const out = {};
    for (const r of filtered) {
      out[r.family] = out[r.family] || [];
      out[r.family].push(r);
    }
    return out;
  }, [reqs, familyFilter]);

  const familyLabel = (k) => families.find(f => f.key === k)?.label || k;

  const remove = async (rid) => {
    if (!window.confirm('Delete this requirement?')) return;
    await fetchSln(`/api/prioris/ma/spec/requirements/${rid}`, { method: 'DELETE' });
    await load();
  };

  return (
    <div className="px-5 py-4" data-testid="ma-spec-library" style={{ color: t.text }}>
      <div className="flex items-start gap-3 mb-3">
        <ShieldCheck className="w-4 h-4 flex-shrink-0 mt-1" style={{ color: t.accent }} />
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-semibold tracking-wider" style={{ color: t.accent }}>
            MATERIALS APPROVAL · SPEC LIBRARY · {(project.asset_name || project.name || '').toUpperCase()}
          </div>
          <h2 className="text-base font-bold">Project Specifications</h2>
          <p className="text-[11px]" style={{ color: t.textDim }}>
            Rules from the project spec. Every submittal is auto-checked against matching rules.
            Rules with an exposure class only apply to submittals that declare that class.
          </p>
        </div>
        <div className="flex gap-2">
          {isAdmin && (
            <button onClick={onOpenPdfIngest}
              className="px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"
              style={{ background: t.inputBg, border: `1px dashed ${t.cardBorder}`, color: t.textMuted }}
              title="Ingest a project spec PDF — coming soon" data-testid="spec-ingest-pdf">
              <FileUp className="w-3 h-3" /> Ingest PDF <Sparkles className="w-2.5 h-2.5" style={{ color: t.accent }} />
            </button>
          )}
          {isAdmin && (
            <button onClick={() => { setShowAdd(true); setEditingId(null); }}
              className="px-2 py-1 rounded text-[11px] font-semibold inline-flex items-center gap-1"
              style={{ background: t.accent, color: '#000' }}
              data-testid="spec-add-rule">
              <Plus className="w-3 h-3" /> Requirement
            </button>
          )}
        </div>
      </div>

      <div className="mb-3 flex items-center gap-2">
        <span className="text-[11px]" style={{ color: t.textDim }}>Filter:</span>
        <select value={familyFilter} onChange={e => setFamilyFilter(e.target.value)}
          className="rounded px-2 py-1 text-[11px]"
          style={{ background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text }}
          data-testid="spec-family-filter">
          <option value="">All families</option>
          {families.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
        </select>
        <span className="text-[11px] ml-auto" style={{ color: t.textDim }}>{reqs.length} rules total</span>
      </div>

      {loading ? (
        <div className="py-8 flex justify-center"><Loader2 className="w-5 h-5 animate-spin" style={{ color: t.accent }} /></div>
      ) : reqs.length === 0 ? (
        <div className="border border-dashed rounded-lg py-10 text-center" style={{ borderColor: t.cardBorder }}>
          <ShieldCheck className="w-7 h-7 mx-auto mb-2" style={{ color: t.textDim }} />
          <div className="text-[12px] font-semibold mb-1">No rules yet</div>
          <div className="text-[11px] mb-4" style={{ color: t.textDim }}>
            Add project specification requirements so every submittal is auto-judged. Start with concrete mix requirements per exposure class.
          </div>
          {isAdmin && (
            <button onClick={() => setShowAdd(true)}
              className="px-3 py-1.5 rounded text-[12px] font-semibold inline-flex items-center gap-1"
              style={{ background: t.accent, color: '#000' }}>
              <Plus className="w-3 h-3" /> Add first requirement
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {Object.entries(grouped).map(([fam, list]) => (
            <div key={fam} className="rounded border" style={{ borderColor: t.cardBorder }}>
              <div className="px-3 py-1.5 flex items-center gap-2" style={{ background: t.inputBg, borderBottom: `1px solid ${t.cardBorder}` }}>
                <ChevronRight className="w-3 h-3" style={{ color: t.accent }} />
                <span className="text-[12px] font-bold">{familyLabel(fam)}</span>
                <span className="text-[11px]" style={{ color: t.textDim }}>{list.length} rule{list.length === 1 ? '' : 's'}</span>
              </div>
              <table className="w-full text-[11px]">
                <thead>
                  <tr style={{ color: t.textMuted }}>
                    {['Field', 'Rule', 'Exposure', 'Element', 'Standard', ''].map(h =>
                      <th key={h} className="px-2 py-1 text-left font-semibold" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>{h}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {list.map(r => (
                    <tr key={r.id} data-testid={`spec-row-${r.id}`}>
                      <td className="px-2 py-1" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                        <div style={{ color: t.text, fontWeight: 600 }}>{r.label || r.field}</div>
                        <code className="text-[10px]" style={{ color: t.textDim }}>{r.field}</code>
                      </td>
                      <td className="px-2 py-1" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.text }}>
                        {ruleSummary(r)}
                      </td>
                      <td className="px-2 py-1" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textMuted }}>
                        {r.exposure_class || '—'}
                      </td>
                      <td className="px-2 py-1" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textMuted }}>
                        {r.element_type || '—'}
                      </td>
                      <td className="px-2 py-1" style={{ borderBottom: `1px solid ${t.cardBorder}`, color: t.textDim }}>
                        {r.standard_ref || '—'}
                      </td>
                      <td className="px-1 py-1 text-right" style={{ borderBottom: `1px solid ${t.cardBorder}` }}>
                        {isAdmin && (
                          <button onClick={() => { setEditingId(r.id); setShowAdd(true); }}
                            className="p-1 rounded hover:bg-white/5 text-[10px]" style={{ color: t.accent }}
                            data-testid={`spec-edit-${r.id}`}>edit</button>
                        )}
                        {isAdmin && (
                          <button onClick={() => remove(r.id)}
                            className="p-1 rounded hover:bg-red-500/10"
                            data-testid={`spec-del-${r.id}`}>
                            <Trash2 className="w-2.5 h-2.5" style={{ color: '#f87171' }} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))}
        </div>
      )}

      {showAdd && (
        <RequirementEditor
          t={t}
          projectId={projectId}
          families={families}
          existing={editingId ? reqs.find(r => r.id === editingId) : null}
          onClose={() => { setShowAdd(false); setEditingId(null); }}
          onSaved={async () => { setShowAdd(false); setEditingId(null); await load(); }}
        />
      )}
    </div>
  );
}

function ruleSummary(r) {
  if (r.operator === 'between') return `between ${r.value} – ${r.value_max} ${r.unit || ''}`.trim();
  if (r.operator === 'in' || r.operator === 'not_in') {
    const list = (r.value_list || []).join(', ');
    return `${r.operator === 'in' ? 'in' : 'not in'} { ${list} }`;
  }
  const map = { '>=':'≥', '<=':'≤', '=':'=', '!=':'≠' };
  return `${map[r.operator] || r.operator} ${r.value} ${r.unit || ''}`.trim();
}

/* ═══════════════ requirement editor ═══════════════ */
function RequirementEditor({ t, projectId, families, existing, onClose, onSaved }) {
  const [family, setFamily] = useState(existing?.family || 'concrete_mix');
  const [field, setField] = useState(existing?.field || '');
  const [label, setLabel] = useState(existing?.label || '');
  const [operator, setOperator] = useState(existing?.operator || '>=');
  const [value, setValue] = useState(existing?.value ?? '');
  const [valueMax, setValueMax] = useState(existing?.value_max ?? '');
  const [valueList, setValueList] = useState((existing?.value_list || []).join(', '));
  const [unit, setUnit] = useState(existing?.unit || '');
  const [standardRef, setStandardRef] = useState(existing?.standard_ref || '');
  const [exposure, setExposure] = useState(existing?.exposure_class || '');
  const [elementType, setElementType] = useState(existing?.element_type || '');
  const [sourceNote, setSourceNote] = useState(existing?.source_note || '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  const fam = families.find(f => f.key === family);
  const hint = FAMILY_FIELD_HINTS[family] || [];
  const customHints = (fam?.custom_fields || []).map(cf => ({ key: cf.name, label: `${cf.label || cf.name} (custom)` }));
  const allHints = [...hint, ...customHints];

  const save = async () => {
    setSaving(true); setErr('');
    const body = {
      project_id: projectId,
      family, field, label, operator, unit, standard_ref: standardRef,
      exposure_class: exposure, element_type: elementType, source_note: sourceNote,
      value: ['>=','<=','=','!=','between'].includes(operator) ? value : null,
      value_max: operator === 'between' ? valueMax : null,
      value_list: ['in','not_in'].includes(operator)
        ? valueList.split(',').map(s => s.trim()).filter(Boolean) : [],
    };
    try {
      if (existing) {
        await fetchSln(`/api/prioris/ma/spec/requirements/${existing.id}`, {
          method: 'PATCH', body: JSON.stringify(body),
        });
      } else {
        await fetchSln('/api/prioris/ma/spec/requirements', {
          method: 'POST', body: JSON.stringify(body),
        });
      }
      onSaved();
    } catch (e) { setErr(e.message); }
    setSaving(false);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.6)' }} onClick={onClose} data-testid="spec-edit-modal">
      <div className="rounded-lg w-full max-w-lg p-4" style={{ background: t.card, border: `1px solid ${t.cardBorder}`, color: t.text }} onClick={e => e.stopPropagation()}>
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4" style={{ color: t.accent }} />
          <h3 className="text-sm font-bold">{existing ? 'Edit' : 'Add'} requirement</h3>
          <button onClick={onClose} className="ml-auto p-1 rounded hover:bg-white/5">
            <X className="w-3.5 h-3.5" style={{ color: t.textMuted }} />
          </button>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <FormRow t={t} label="Family">
            <select value={family} onChange={e => setFamily(e.target.value)} style={inp(t)} data-testid="spec-form-family">
              {families.map(f => <option key={f.key} value={f.key}>{f.label}</option>)}
            </select>
          </FormRow>
          <FormRow t={t} label="Field">
            <input list="field-hints" value={field} onChange={e => setField(e.target.value)}
              placeholder="e.g. cement_content, _wcm, la_value"
              style={inp(t)} data-testid="spec-form-field" />
            <datalist id="field-hints">
              {allHints.map(h => <option key={h.key} value={h.key}>{h.label}</option>)}
            </datalist>
          </FormRow>
          <FormRow t={t} label="Display label">
            <input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. Min cement content (XD3)" style={inp(t)} data-testid="spec-form-label" />
          </FormRow>
          <FormRow t={t} label="Operator">
            <select value={operator} onChange={e => setOperator(e.target.value)} style={inp(t)} data-testid="spec-form-operator">
              {OPERATORS.map(o => <option key={o.v} value={o.v}>{o.label}</option>)}
            </select>
          </FormRow>
          {['>=','<=','=','!='].includes(operator) && (
            <>
              <FormRow t={t} label="Value">
                <input value={value} onChange={e => setValue(e.target.value)} style={inp(t)} data-testid="spec-form-value" />
              </FormRow>
              <FormRow t={t} label="Unit">
                <input value={unit} onChange={e => setUnit(e.target.value)} placeholder="kg/m³, %, MPa…" style={inp(t)} data-testid="spec-form-unit" />
              </FormRow>
            </>
          )}
          {operator === 'between' && (
            <>
              <FormRow t={t} label="Min"><input value={value} onChange={e => setValue(e.target.value)} style={inp(t)} /></FormRow>
              <FormRow t={t} label="Max"><input value={valueMax} onChange={e => setValueMax(e.target.value)} style={inp(t)} /></FormRow>
              <FormRow t={t} label="Unit"><input value={unit} onChange={e => setUnit(e.target.value)} style={inp(t)} /></FormRow>
            </>
          )}
          {['in','not_in'].includes(operator) && (
            <FormRow t={t} label="Allowed values" full>
              <input value={valueList} onChange={e => setValueList(e.target.value)}
                placeholder="comma-separated · e.g. CEM I, CEM II/A-S, CEM III/A"
                style={inp(t)} data-testid="spec-form-value-list" />
            </FormRow>
          )}
          <FormRow t={t} label="Exposure class">
            <input value={exposure} onChange={e => setExposure(e.target.value)} placeholder="blank = all · e.g. XD3, XS2" style={inp(t)} data-testid="spec-form-exposure" />
          </FormRow>
          <FormRow t={t} label="Element type">
            <input value={elementType} onChange={e => setElementType(e.target.value)} placeholder="blank = all · e.g. Pile" style={inp(t)} />
          </FormRow>
          <FormRow t={t} label="Standard reference" full>
            <input value={standardRef} onChange={e => setStandardRef(e.target.value)}
              placeholder="e.g. UFGS 03 31 29 Table 2 · EN 206 Cl. 8"
              style={inp(t)} data-testid="spec-form-standard" />
          </FormRow>
          <FormRow t={t} label="Source note" full>
            <input value={sourceNote} onChange={e => setSourceNote(e.target.value)}
              placeholder="free text — section quote, page ref, etc." style={inp(t)} />
          </FormRow>
        </div>

        {err && (
          <div className="mt-3 px-2 py-1 text-[11px] rounded flex items-center gap-2" style={{ background: 'rgba(239,68,68,0.1)', color: '#f87171' }}>
            <AlertCircle className="w-3 h-3" /> {err}
          </div>
        )}

        <div className="flex justify-end gap-2 mt-4">
          <button onClick={onClose} className="px-3 py-1 rounded text-[11px]" style={{ background: t.inputBg, color: t.textMuted }}>Cancel</button>
          <button onClick={save} disabled={saving}
            className="px-3 py-1 rounded text-[11px] font-semibold inline-flex items-center gap-1"
            style={{ background: t.accent, color: '#000' }}
            data-testid="spec-form-save">
            {saving ? <Loader2 className="w-3 h-3 animate-spin" /> : <Check className="w-3 h-3" />}
            {existing ? 'Save' : 'Add requirement'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FormRow({ t, label, children, full }) {
  return (
    <div className={full ? 'col-span-2' : ''}>
      <div className="text-[10px] uppercase tracking-wider mb-1" style={{ color: t.textDim }}>{label}</div>
      {children}
    </div>
  );
}
const inp = (t) => ({ width: '100%', background: t.inputBg, border: `1px solid ${t.inputBorder}`, color: t.text, fontSize: 11, padding: '4px 8px', borderRadius: 4 });
